/**
 * Single source of truth for parameter dependencies
 * Used by: ParameterConfigPanel (UI disabling), NewStudyModern (request processing)
 *
 * This maps numeric parameters to their controlling boolean filter parameters.
 * When a filter is pinned to OFF (false), its dependent numeric parameters
 * should be disabled in the UI and excluded from optimization requests.
 */

/**
 * Maps numeric parameter keys to their controlling boolean filter parameter keys.
 * When the controlling filter is pinned to OFF, the numeric parameter should be disabled.
 */
export const NUMERIC_PARAM_TO_FILTER: Record<string, string> = {
  regimethreshold: "useregimefilter",
  adxthreshold: "useadxfilter",
  emaperiod: "useemafilter",
  smaperiod: "usesmafilter",
  // Kernel smoothing dependencies
  // Note: usekernelsmoothing is currently fixed to true with no UI toggle,
  // but these mappings are ready for when a toggle is added.
  lookbackwindow: "usekernelsmoothing",
  relativeweight: "usekernelsmoothing",
  regressionlevel: "usekernelsmoothing",
  crossoverlag: "usekernelsmoothing",
};

/**
 * Human-readable display names for filter parameters.
 * Used in disabled reason messages shown to users.
 */
export const FILTER_DISPLAY_NAMES: Record<string, string> = {
  useregimefilter: "Regime Filter",
  useadxfilter: "ADX Filter",
  useemafilter: "EMA Filter",
  usesmafilter: "SMA Filter",
  usekernelsmoothing: "Kernel Smoothing",
};

/**
 * Result of checking whether a parameter should be disabled.
 */
export interface DisabledState {
  /** Whether the parameter should be disabled */
  disabled: boolean;
  /** Human-readable reason why the parameter is disabled (only set when disabled is true) */
  reason?: string;
}

/**
 * Checks if a parameter should be disabled based on its dependency on a boolean filter.
 *
 * A numeric parameter is disabled when:
 * 1. It has a controlling filter (defined in NUMERIC_PARAM_TO_FILTER)
 * 2. The controlling filter is pinned (not being optimized)
 * 3. The controlling filter value is false (OFF)
 *
 * @param paramKey - The parameter key to check (e.g., "regimethreshold")
 * @param pinnedParameters - Map of parameter keys to their pinned state
 * @param parameterValues - Map of parameter keys to their current values
 * @returns DisabledState indicating if disabled and why
 */
export function isParameterDisabled(
  paramKey: string,
  pinnedParameters: Record<string, boolean>,
  parameterValues: Record<string, unknown>
): DisabledState {
  const controllingFilter = NUMERIC_PARAM_TO_FILTER[paramKey];

  // If this parameter doesn't have a controlling filter, it's never disabled
  if (!controllingFilter) {
    return { disabled: false };
  }

  const isPinned = pinnedParameters[controllingFilter] ?? false;
  const filterValue = parameterValues[controllingFilter];

  // Disable if the controlling filter is pinned to OFF (false)
  if (isPinned && filterValue === false) {
    const filterDisplayName =
      FILTER_DISPLAY_NAMES[controllingFilter] || controllingFilter;
    return {
      disabled: true,
      reason: `${filterDisplayName} is OFF`,
    };
  }

  return { disabled: false };
}

/**
 * Filters out dependent parameters whose controlling boolean filter is
 * explicitly disabled.
 *
 * Used on the results page to hide parameters that are not meaningful when
 * their controlling filter was off. For example, if `useemafilter` is false,
 * there's no point showing `emaperiod` since it wasn't used.
 *
 * Semantics — a dependent parameter is hidden ONLY when its controlling filter
 * is **present and explicitly off** (`false` or `0`). An *absent* controller is
 * treated as enabled, because absence does not mean "off":
 *
 * - `usekernelsmoothing` is fixed to `true` with no UI toggle, so it is never an
 *   optimized parameter and never appears in a trial's `params`. Its dependents
 *   (`lookbackwindow`, `relativeweight`, `regressionlevel`, `crossoverlag`) must
 *   still be shown.
 * - A filter the user pinned/fixed to `true` is stored as a fixed parameter and
 *   likewise won't appear in the optimized `params`; its dependent must be shown.
 *
 * Hiding on absence would drop valid, in-use parameters from the results view
 * (regression observed for the four kernel parameters above).
 *
 * @param params - The parameter key-value pairs from a trial result
 * @returns Filtered params with explicitly-disabled dependent parameters removed
 */
export function filterDisabledDependentParams<T>(
  params: Record<string, T>
): Record<string, T> {
  const filtered: Record<string, T> = {};

  for (const [key, value] of Object.entries(params)) {
    const controllingFilter = NUMERIC_PARAM_TO_FILTER[key];

    if (!controllingFilter) {
      // Independent parameter — always keep.
      filtered[key] = value;
      continue;
    }

    const filterValue = params[controllingFilter];
    // Hide only when the controlling filter is present AND explicitly off.
    // An absent controller (undefined) means it was not optimized — e.g. a
    // fixed-true filter or always-on kernel smoothing — so keep the dependent.
    const isFilterExplicitlyOff = filterValue === false || filterValue === 0;

    if (!isFilterExplicitlyOff) {
      filtered[key] = value;
    }
  }

  return filtered;
}
